import { Table } from "reactstrap";
import useUser from "../../../customHooks/useUser";
import Layout from "../../custom/Layout";
import useAwbs, { CounterAwb } from "../import/useAwbs";
import { IQueue, IRFS } from "../../../globals/interfaces";
import styled from "styled-components";
import _ from "lodash";
import { Button } from "reactstrap";
import { formatBoolean, formatDatetime, getTsDate } from "../../../utils";
import apiClient from "../../../apiClient";
import { useState } from "react";
import useLoading from "../../../customHooks/useLoading";
import Card from "../../custom/Card";

export default function RFS() {

    const { user: { s_email, s_unit, s_first_name, s_last_name } } = useUser();
    const { setLoading } = useLoading();
    const [refresh, setRefresh] = useState(false);
    const { awbsLoading, awbs } = useAwbs(
        s_email,
        s_unit,
        "RFS",
        refresh
    );

    const awb: Partial<CounterAwb> = _.get(awbs, '[0]', {});
    const rfs: Partial<IRFS> = _.get(awb, 'rfs', {});

    const [idValidation, setIdValidation] = useState(false);

    const sendRfsToWarehouse = async () => {
        setLoading(true);
        const now = getTsDate();
        const queueData = {
            id: awb.id,
            t_counter_start: now,
            t_counter_end: now,
            s_type: 'RFS',
            s_counter_ownership_agent: s_email,
            s_transaction_id: awb.s_transaction_id,
            s_status: 'DOCUMENTED',
            s_modified_by: s_email,
            t_modified: now
        };

        const additional = {
            s_unit,
            s_mawb_id: awb.s_mawb_id,
            counterAgentName: `${s_first_name} ${s_last_name}`
        }

        const data = {
            queueData,
            additional
        }

        await apiClient.post(`/rfs`, data);
        setRefresh(prev => !prev);
        setLoading(false);
    }

    return (
        <Layout>
            <h1>RFS</h1>
            <FlexContainer>
                <div>
                    <Card>
                        <CustomTable striped>
                            <h6>1) Information</h6>
                            <tbody>
                                <tr>
                                    <td>Arrival Time</td>
                                    <td>{formatDatetime(awb.t_created)}</td>
                                </tr>
                                <tr>
                                    <td>Truck company</td>
                                    <td>{awb.s_trucking_company}</td>
                                </tr>
                                <tr>
                                    <td>Driver name</td>
                                    <td>{awb.s_trucking_driver}</td>
                                </tr>
                                <tr>
                                    <td>Phone Number</td>
                                    <td>{awb.s_trucking_cell}</td>
                                </tr>
                                <tr>
                                    <td>Vehicle Type</td>
                                    <td>{rfs.vehicleType}</td>
                                </tr>
                                <tr>
                                    <td>ID Validation</td>
                                    <td>
                                        <Button onClick={() => setIdValidation(true)}>
                                            Confirmed
                                            {
                                                idValidation && (
                                                    <i className={'fas fa-check ml-2'} />
                                                )
                                            }
                                        </Button>
                                    </td>
                                </tr>
                            </tbody>
                        </CustomTable>
                    </Card>
                    <Card className={'mt-2'}>
                        <h6>2) Shipment Type</h6>
                        <p>{rfs.cargoHandlingType}</p>
                    </Card>
                </div>
                <ShipmentDetails>
                    <Card>
                        <h6>3) Shipment Details</h6>
                        <CustomTable striped>
                            <tbody>
                                <tr>
                                    <td>Airline Truck Number</td>
                                    <td>{rfs.truckNumber}</td>
                                </tr>
                                <tr>
                                    <td>Truck Type</td>
                                    <td>{rfs.truckType}</td>
                                </tr>
                                <tr>
                                    <td>Origin</td>
                                    <td>{rfs.origin}</td>
                                </tr>
                                <tr>
                                    <td>Destination</td>
                                    <td>{rfs.destination}</td>
                                </tr>
                                <tr>
                                    <td>Type</td>
                                    <td>{rfs.type}</td>
                                </tr>
                                <tr>
                                    <td>Fuel Type</td>
                                    <td>{rfs.fuelType}</td>
                                </tr>
                                <tr>
                                    <td>Vehicle Type</td>
                                    <td>{rfs.vehicleType}</td>
                                </tr>
                                <tr>
                                    <td>Chain of Custody</td>
                                    <td className={rfs.chainOfCustodyStatus !== 'intact' && 'bg-warning'}>{rfs.chainOfCustodyStatus}</td>
                                </tr>
                                <ShipmentInformationDetails rfs={rfs} />
                            </tbody>
                        </CustomTable>
                    </Card>
                    <FlexEnd>
                        <Button
                            onClick={sendRfsToWarehouse}
                            disabled={!idValidation}
                        >
                            Send to Warehouse
                        </Button>
                    </FlexEnd>
                </ShipmentDetails>
            </FlexContainer>
        </Layout>
    );
}

type RenderArray = Array<{
    name: string;
    value: keyof IRFS;
}>

const ShipmentInformationDetails = ({
    rfs
}: {
    rfs: Partial<IRFS>;
}) => {

    if (rfs.truckType === 'CDT') {
        const array: RenderArray = [{
            name: 'Roller-bed equipped',
            value: 'rollerBedEquipped'
        }, {
            name: 'Hazmat Certified',
            value: 'hazmatCertified'
        }];

        return (
            <>
                <RenderQuestions rfs={rfs} array={array} />
                {
                    rfs.cargoHandlingType === 'dropoff' && (
                        <tr>
                            <td>V Number</td>
                            <td>{rfs.vNumber}</td>
                        </tr>
                    )
                }
            </>
        );
    }

    if (rfs.truckType === 'EUV') {
        const array: RenderArray = [{
            name: 'Roller-bed equipped',
            value: 'rollerBedEquipped'
        }, {
            name: 'Hazmat Certified',
            value: 'hazmatCertified'
        }];
        return (
            <RenderQuestions rfs={rfs} array={array} />
        );
    }

    if (rfs.truckType === 'LTL') {
        const array: RenderArray = [{
            name: 'Hazmat Certified',
            value: 'hazmatCertified'
        }];
        return (
            <RenderQuestions rfs={rfs} array={array} />
        );
    }

    if (rfs.truckType === 'Mail Truck') {
        const array: RenderArray = [{
            name: 'Certified by USPS to handle USPS Mail',
            value: 'certifiedToUspsMail'
        }];

        return (
            <RenderQuestions rfs={rfs} array={array} />
        );
    }

    if (rfs.truckType === 'Reefer') {
        const array: RenderArray = [{
            name: 'Hazmat Certified',
            value: 'hazmatCertified'
        }, {
            name: 'Pharma Temp Controlled Approved',
            value: 'pharmaTempControlledApproved'
        }];

        return (
            <>
                <RenderQuestions rfs={rfs} array={array} />
                <tr>
                    <td>Precooled Temperature</td>
                    <td>{rfs.preCooledTemperature}</td>
                </tr>
            </>
        );
    }

    return null;
}

const RenderQuestions = ({
    rfs,
    array
}: {
    rfs: Partial<IRFS>;
    array: RenderArray;
}) => {

    const renderItem = (key: keyof IRFS): string => {
        if (typeof rfs[key] === 'boolean') {
            // @ts-ignore
            return formatBoolean(rfs[key]);
        } else if (typeof rfs[key] === 'string') {
            return String(rfs[key]);
        }
        return null;
    }

    const renderWarning = (key: keyof IRFS) => typeof rfs[key] === 'boolean' && !rfs[key];

    return (
        <>
            {
                array.map((arrItem, i) => (
                    <tr key={i}>
                        <td>{arrItem.name}</td>
                        <td className={renderWarning(arrItem.value) ? 'bg-warning' : ''}>{renderItem(arrItem.value)}</td>
                    </tr>
                ))
            }
        </>
    );
}

const CustomTable = styled(Table)`
    /* width: 500px; */
    
    td {
        text-transform: uppercase;
    }
`;

const FlexContainer = styled.div`
    display: flex;
    gap: 10px;

    & div:first-child {
        flex: 2;
    }

    p {
        text-transform: uppercase;
    }
`;

const ShipmentDetails = styled.div`
    flex: 3;
`;

const FlexEnd = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-top: 15px;
`;